import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import AsSeenOn from '@/components/common/AsSeenOn';
import { LongVerticalLine, StepsLine } from '@/components/common/Icons';
import ShareButton from '@/components/common/ShareButton';
import Breadcrumbs from '@/components/navigation/Breadcrumbs';
import { useSingleRecipePost } from '@/services/blogService';

function convertToISO8601Duration(timeStr: string): string {
    const regex = /(?:(\d+)\s*hour[s]?)?\s*(?:(\d+)\s*minute[s]?)?/i;
    const match = timeStr.toLowerCase().match(regex);

    if (!match) return '';

    const hours = match[1] ? `PT${match[1]}H` : 'PT';
    const minutes = match[2] ? `${match[2]}M` : '';

    return hours + minutes;
}

function parseDuration(duration: string) {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return { hours: 0, minutes: 0 };

    return {
        hours: parseInt(match[1] || '0', 10),
        minutes: parseInt(match[2] || '0', 10),
    };
}

function combineDurations(duration1: string, duration2: string): string {
    const d1 = parseDuration(duration1);
    const d2 = parseDuration(duration2);

    let totalMinutes = d1.minutes + d2.minutes;
    let totalHours = d1.hours + d2.hours;

    if (totalMinutes >= 60) {
        totalHours += Math.floor(totalMinutes / 60);
        totalMinutes %= 60;
    }

    let result = 'PT';
    if (totalHours) result += `${totalHours}H`;
    if (totalMinutes) result += `${totalMinutes}M`;
    return result;
}

function extractIngredients(htmlString: string): string[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const ingredients = Array.from(doc.querySelectorAll('p')).map(
        (p) => p.textContent?.trim().replace(/^- /, '') || ''
    );

    return ingredients.filter((line) => line.length > 0);
}

function extractDirectionsToSteps(
    htmlString: string
): { '@type': string; text: string }[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const steps: { '@type': string; text: string }[] = [];
    let currentStep = '';

    const paragraphs = Array.from(doc.querySelectorAll('p')).map(
        (p) => p.textContent?.trim() || ''
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const line of paragraphs) {
        if (/^\d+\.\s*/.test(line)) {
            if (currentStep) {
                steps.push({ '@type': 'HowToStep', text: currentStep.trim() });
            }
            currentStep = line.replace(/^\d+\.\s*/, '');
        } else {
            currentStep += ` ${line}`;
        }
    }

    if (currentStep) {
        steps.push({ '@type': 'HowToStep', text: currentStep.trim() });
    }

    return steps;
}

export default function SingleRecipePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { singleRecipePostResult, isLoading } = useSingleRecipePost(slug);

    if (isLoading && !singleRecipePostResult) return null;

    const routes = [
        {
            route: '/blog',
            title: 'Blog',
        },
        {
            route: `${singleRecipePostResult?.slug}`,
            title: `${singleRecipePostResult?.title}`,
        },
    ];

    const preparationTime = convertToISO8601Duration(
        singleRecipePostResult.preparationTime
    );
    const cookingTime = convertToISO8601Duration(
        singleRecipePostResult.cookingTime
    );

    const total = combineDurations(preparationTime, cookingTime);

    return (
        <>
            <Helmet>
                <title>
                    {`${singleRecipePostResult?.title} | Recipes | ButcherCrowd`}
                </title>
                <meta
                    name="description"
                    content="Curated or customised meat & seafood boxes delivered for your everyday cooking essentials"
                />
                <meta
                    property="og:title"
                    content={`${singleRecipePostResult?.title} | Blog | ButcherCrowd`}
                />
                {singleRecipePostResult && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org/',
                            '@type': 'Recipe',
                            name: singleRecipePostResult.title,
                            image: singleRecipePostResult.thumbnail.url,
                            description: singleRecipePostResult.description,
                            author: {
                                '@type': 'Person',
                                name: 'ButcherCrowd',
                            },
                            datePublished: singleRecipePostResult.createdAt,
                            prepTime: preparationTime,
                            cookTime: cookingTime,
                            totalTime: total,
                            recipeIngredient: extractIngredients(
                                singleRecipePostResult.ingredients
                            ),
                            recipeInstructions: extractDirectionsToSteps(
                                singleRecipePostResult.directions
                            ),
                        })}
                    </script>
                )}
            </Helmet>
            <div>
                <div className="hidden px-8 pt-8 sm:flex sm:justify-between lg:mx-7">
                    <Breadcrumbs crumbs={routes} />
                    {singleRecipePostResult?.author?.name && (
                        <div className="flex">
                            <div>By {singleRecipePostResult?.author?.name}</div>
                            {singleRecipePostResult?.author?.avatar && (
                                <div>
                                    <img
                                        src={
                                            singleRecipePostResult?.author
                                                ?.avatar
                                        }
                                        alt={`${singleRecipePostResult?.author?.name} avatar`}
                                        className="ml-2 w-[30px] rounded-2xl"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="sm:flex sm:flex-col-reverse">
                    <h1 className="mt-12 text-center text-5xl font-medium uppercase sm:mt-8">
                        {singleRecipePostResult?.title}
                    </h1>
                    {singleRecipePostResult?.categories && (
                        <div className="mt-4 flex flex-wrap justify-center space-x-2 sm:mt-9">
                            {singleRecipePostResult?.categories?.map(
                                (e: any) => (
                                    <button
                                        key={e.id}
                                        type="button"
                                        className="bg-secondary px-5 py-1.5 font-agrandir text-sm font-bold uppercase text-white transition-colors hover:bg-red-600"
                                        onClick={() => {
                                            navigate(`/blog-recipes/${e.slug}`);
                                        }}
                                    >
                                        {e.name}
                                    </button>
                                )
                            )}
                        </div>
                    )}
                    <div className="mt-5 flex flex-wrap justify-center">
                        <div className="border border-black px-3 py-1 text-center font-agrandir text-sm font-bold uppercase">
                            Recipe
                        </div>
                    </div>
                </div>
                <div className="mt-5 flex flex-col items-center sm:flex-row sm:justify-center">
                    <div className="font-agrandir text-xs font-bold uppercase tracking-wide">
                        Preparation {singleRecipePostResult?.preparationTime}
                    </div>
                    <StepsLine className="h-1 w-10 text-primary sm:px-2" />
                    <div className="font-agrandir text-xs font-bold uppercase tracking-wide">
                        Cooking {singleRecipePostResult?.cookingTime}
                    </div>
                </div>
                <img
                    src={singleRecipePostResult?.thumbnail.url}
                    alt={singleRecipePostResult?.thumbnail.alt}
                    className="my-12 mx-auto h-auto w-[1069px] max-w-[100%] xl:w-[1200px] 2xl:w-[1450px]"
                />
                <div className="mx-auto w-[880px] max-w-[calc(100%_-_40px)]">
                    <div
                        className="tiny mb-8"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: singleRecipePostResult?.content,
                        }}
                    />
                    <div className="relative flex w-full flex-col justify-between md:flex-row">
                        <div className="mb-6 w-full md:w-[calc(50%_-_30px)]">
                            <div className="text-xl font-bold text-primary">
                                Ingredients
                            </div>
                            <div
                                className="tiny tiny--tight col-span-2 mt-4 max-w-none text-black"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: singleRecipePostResult?.ingredients,
                                }}
                            />
                        </div>
                        <div className="left-[50%] hidden h-full w-[10px] translate-x-[-50%] overflow-hidden md:absolute md:block">
                            <div className="absolute top-0 left-0 flex max-h-full items-end">
                                <LongVerticalLine className="h-[500px]" />
                            </div>
                        </div>
                        <div className="mb-4 w-full md:w-[calc(50%_-_30px)]">
                            <div className="mt-4 text-xl font-bold text-primary sm:mt-0">
                                Directions
                            </div>
                            <div
                                className="tiny tiny--tight prose prose-sm col-span-2 mt-4 max-w-none text-black sm:mr-2"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: singleRecipePostResult?.directions,
                                }}
                            />
                        </div>
                    </div>
                    <ShareButton label="Share this recipe" />
                </div>
                <AsSeenOn />
            </div>
        </>
    );
}
